import { c as _c } from "react/compiler-runtime";
import { HotelCardLink, HotelCardLinkProperties, hotelCardOnClick } from "@website/accommodation-ui/hotel-card-new/hotel-card-link";
import { HotelCardThumbnail } from "@website/accommodation-ui/hotel-card-new/hotel-card-thumbnail";
import { HotelCardPropertiesType } from "@website/accommodation-ui/hotel-card-new/hotel-card.types";
import { useCompatRouter } from "@website/hooks/compat-router";
import { useIsVisible } from "@website/hooks/is-visible";
import { useViewPort } from "@website/hooks/viewport";
import { addEventToDataLayer } from "@website/tracking/tracking.gtm";
import { DATA_LAYER_EVENTS, USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { ApplicationNameType, addEventToTracking, mapProductForUserTracking } from "@website/tracking/user";
import { Button } from "@website/ui/button";
import { MiniPopover } from "@website/ui/mini-popover";
import omit from "lodash-es/omit";
import { FC, useEffect, useRef, useState } from "react";
import { HotelSnappPayBadge } from "../hotel-snapp-pay";
import { HotelCardAction } from "./hotel-card-action";
import { HotelCardPricing } from "./hotel-card-action/hotel-card-pricing";
import { HotelCardInfo } from "./hotel-card-info";
const appName = process.env.NEXT_PUBLIC_APP_NAME as ApplicationNameType;
const HOTEL_CARD_VIEW_IGNORED_QUERY_KEYS: ReadonlySet<string> = new Set(["filters_visible", "item", "modal", "popover", "selectedHotelId"]);
const getHotelCardSearchKey = (pathname: string | null | undefined, query: Record<string, string | Array<string> | undefined>) => JSON.stringify([pathname ?? "", Object.entries(query).filter(([key]) => !HOTEL_CARD_VIEW_IGNORED_QUERY_KEYS.has(key)).sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))]);
export const HotelCardDivider: FC<Pick<HotelCardPropertiesType, "size">> = t0 => {
  const $ = _c(8);
  const {
    size
  } = t0;
  const t1 = `block h-px w-full ${size === "small" ? "" : "lg:hidden"}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      background: "repeating-linear-gradient(to right, #EBECF2 0, #EBECF2 4px, transparent 4px, transparent 8px)"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1) {
    t3 = <div className={t1} style={t2} />;
    $[1] = t1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== size) {
    t4 = size !== "small" && <div className="h-full w-px" style={{
      background: "repeating-linear-gradient(to bottom, #EBECF2 0, #EBECF2 4px, transparent 4px, transparent 8px)"
    }} />;
    $[3] = size;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
export const HotelCard: FC<HotelCardPropertiesType> = properties => {
  const $ = _c(148);
  const {
    isMobile
  } = useViewPort();
  const [clickLoading, setClickLoading] = useState(0);
  const isHomePage = process.env.NEXT_PUBLIC_APP_NAME === "homepage" || process.env.NEXT_PUBLIC_APP_NAME === "website";
  const {
    className,
    loading,
    hotelInfo,
    size: t0,
    index,
    replace,
    snappPayData,
    isSuggestedCard,
    utm,
    onMouseEnter,
    onMouseLeave,
    onLinkClick,
    targetLink,
    showMap,
    shouldRedirectUsingJs,
    showCarousel: t1,
    isLanding,
    homePageSectionName,
    isHotelDomHomePage,
    isPriorityImage,
    isVillaHomePage: t2,
    noFollow,
    prefetch,
    isLastMinute,
    actionButtonText
  } = properties;
  const size = t0 === undefined ? "responsive" : t0;
  const showCarousel = t1 === undefined ? true : t1;
  const isVillaHomePage = t2 === undefined ? false : t2;
  const {
    id,
    hotelDetail,
    hotelOffers,
    thumbnail,
    pricing,
    reservationInfo,
    primaryBadge,
    regularBadges,
    advertisementBadge,
    facilities,
    gallery,
    location,
    categories,
    hasSnappProDiscount,
    transportation,
    isInternational
  } = hotelInfo;
  const router = useCompatRouter();
  const {
    isDesktop
  } = useViewPort();
  const {
    reference,
    inView
  } = useIsVisible();
  let t3;
  if ($[0] !== router.pathname || $[1] !== router.query) {
    t3 = getHotelCardSearchKey(router.pathname, router.query);
    $[0] = router.pathname;
    $[1] = router.query;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const hotelCardSearchKey = t3;
  const hotelCardViewTrackingKey = `${hotelInfo.id}:${hotelCardSearchKey}`;
  const lastHotelCardViewTrackingKeyReference = useRef(null);
  const dateFrom = String(router.query.date_from) || "";
  const dateTo = String(router.query.date_to) || "";
  let t4;
  if ($[3] !== router) {
    t4 = () => router.replace(router?.pathname, omit(router?.query, "modal"), {
      shallow: true,
      scroll: false
    });
    $[3] = router;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const onClose = t4;
  let t5;
  if ($[5] !== dateFrom || $[6] !== dateTo || $[7] !== hotelCardViewTrackingKey || $[8] !== hotelInfo.hotelDetail || $[9] !== hotelInfo.id || $[10] !== hotelInfo.pricing || $[11] !== hotelInfo.ruleId || $[12] !== inView || $[13] !== index) {
    t5 = () => {
      if (!inView || isHomePage || lastHotelCardViewTrackingKeyReference.current === hotelCardViewTrackingKey) {
        return;
      }
      lastHotelCardViewTrackingKeyReference.current = hotelCardViewTrackingKey;
      addEventToDataLayer(DATA_LAYER_EVENTS.VIEW_ITEM_LIST, {
        items: [{
          item_name: hotelInfo?.hotelDetail?.hotelTitle,
          item_id: hotelInfo?.id,
          price: hotelInfo?.pricing?.price,
          item_brand: "IRR",
          item_category: hotelInfo.hotelDetail.cityTitle,
          item_category2: hotelInfo?.hotelDetail?.accommodationType,
          item_category3: hotelInfo?.hotelDetail?.stars,
          item_list_name: `هتل های ${hotelInfo.hotelDetail.cityTitle}`,
          item_list_id: hotelInfo.hotelDetail.cityId,
          index
        }]
      });
      const payload = {
        position: index,
        accommodation_id: hotelInfo.id
      };
      if (["villa", "hotel-domestic", "hotel-international"].includes(appName)) {
        addEventToTracking(USER_TRACKING_EVENTS.HOTEL_CARD_VIEWED, {
          date_from: dateFrom,
          date_to: dateTo,
          city: hotelInfo.hotelDetail.cityTitle,
          city_id: hotelInfo.hotelDetail.cityId,
          hotel: hotelInfo.hotelDetail?.hotelTitle,
          hotel_id: hotelInfo.id,
          price: hotelInfo.pricing.price,
          price_off: hotelInfo.pricing.priceOff,
          discount_percent: hotelInfo.pricing.discountPercentage,
          is_available: hotelInfo.hotelDetail.isAvailable,
          price_engine_rules: [{
            id: hotelInfo?.ruleId ?? 0,
            title: ""
          }],
          product: mapProductForUserTracking(appName),
          ...payload
        });
      }
    };
    $[5] = dateFrom;
    $[6] = dateTo;
    $[7] = hotelCardViewTrackingKey;
    $[8] = hotelInfo.hotelDetail;
    $[9] = hotelInfo.id;
    $[10] = hotelInfo.pricing;
    $[11] = hotelInfo.ruleId;
    $[12] = inView;
    $[13] = index;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== dateFrom || $[16] !== dateTo || $[17] !== hotelCardViewTrackingKey || $[18] !== hotelInfo || $[19] !== inView || $[20] !== index) {
    t6 = [dateFrom, dateTo, hotelCardViewTrackingKey, hotelInfo, inView, index, isHomePage];
    $[15] = dateFrom;
    $[16] = dateTo;
    $[17] = hotelCardViewTrackingKey;
    $[18] = hotelInfo;
    $[19] = inView;
    $[20] = index;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  useEffect(t5, t6);
  const t7 = hotelInfo?.hotelDetail?.hotelPageUrl?.href;
  const t8 = hotelInfo?.hotelDetail?.hotelPageUrl?.pathname;
  const t9 = hotelInfo?.hotelDetail?.hotelPageUrl?.query as object;
  let t10;
  if ($[22] !== router.query || $[23] !== t9) {
    t10 = {
      ...router.query,
      ...t9
    };
    $[22] = router.query;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = !isSuggestedCard && replace;
  const t12 = hotelDetail?.hotelTitle;
  let t13;
  if ($[25] !== homePageSectionName || $[26] !== hotelInfo || $[27] !== index || $[28] !== isLastMinute || $[29] !== noFollow || $[30] !== onLinkClick || $[31] !== prefetch || $[32] !== shouldRedirectUsingJs || $[33] !== t10 || $[34] !== t11 || $[35] !== t12 || $[36] !== t7 || $[37] !== t8 || $[38] !== targetLink || $[39] !== utm) {
    t13 = {
      hotelInfo,
      href: t7,
      pathname: t8,
      query: t10,
      setClickLoading,
      index,
      utm,
      replace: t11,
      target: targetLink,
      title: t12,
      shouldRedirectUsingJs,
      children: "",
      onLinkClick,
      homePageSectionName,
      isHomePage,
      isLastMinute,
      noFollow,
      prefetch
    };
    $[25] = homePageSectionName;
    $[26] = hotelInfo;
    $[27] = index;
    $[28] = isLastMinute;
    $[29] = noFollow;
    $[30] = onLinkClick;
    $[31] = prefetch;
    $[32] = shouldRedirectUsingJs;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t7;
    $[37] = t8;
    $[38] = targetLink;
    $[39] = utm;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const linkProperties = t13;
  const t14 = `bg-on-color-high-emphasis shadow-1 flex flex-col overflow-hidden rounded-2xl p-0 sm:p-3 ${className} ${size === "small" ? `${appName === "villa" ? "" : "cursor-pointer"} h-full gap-3` : "md:flex-row"} ${size === "xSmall" ? "h-[118px] w-[272px] cursor-pointer !flex-row items-center gap-2 !rounded-[8px] !p-2 !shadow-none" : ""}`;
  let t15;
  if ($[41] !== id || $[42] !== onMouseEnter) {
    t15 = () => onMouseEnter?.(Number(id));
    $[41] = id;
    $[42] = onMouseEnter;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== id || $[45] !== onMouseLeave) {
    t16 = () => onMouseLeave?.(Number(id));
    $[44] = id;
    $[45] = onMouseLeave;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== homePageSectionName || $[48] !== hotelInfo || $[49] !== index || $[50] !== isLastMinute || $[51] !== onLinkClick || $[52] !== router || $[53] !== size || $[54] !== utm) {
    t17 = size === "xSmall" || size === "small" && appName !== "villa" ? hotelCardOnClick({
      query: utm,
      router,
      setClickLoading,
      index,
      hotelInfo,
      onLinkClick,
      homePageSectionName,
      isHomePage,
      isLastMinute
    }) : undefined;
    $[47] = homePageSectionName;
    $[48] = hotelInfo;
    $[49] = index;
    $[50] = isLastMinute;
    $[51] = onLinkClick;
    $[52] = router;
    $[53] = size;
    $[54] = utm;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  const t18 = hotelOffers?.specialOffer;
  const t19 = hotelDetail?.stars;
  const t20 = hotelDetail?.hotelTitle;
  let t21;
  if ($[56] !== gallery || $[57] !== id || $[58] !== isPriorityImage || $[59] !== primaryBadge || $[60] !== showCarousel || $[61] !== size || $[62] !== t18 || $[63] !== t19 || $[64] !== t20 || $[65] !== thumbnail) {
    t21 = <HotelCardThumbnail gallery={gallery} id={id} isPriorityImage={isPriorityImage} primaryBadge={primaryBadge} showCarousel={showCarousel} size={size} specialOffer={t18} stars={t19} thumbnail={thumbnail} title={t20} />;
    $[56] = gallery;
    $[57] = id;
    $[58] = isPriorityImage;
    $[59] = primaryBadge;
    $[60] = showCarousel;
    $[61] = size;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
    $[65] = thumbnail;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== linkProperties || $[68] !== t21) {
    t22 = <HotelCardLink {...linkProperties} className="md:flex">{t21}</HotelCardLink>;
    $[67] = linkProperties;
    $[68] = t21;
    $[69] = t22;
  } else {
    t22 = $[69];
  }
  const t23 = `flex min-w-0 shrink grow flex-col ${size === "xSmall" ? "h-full" : ""}`;
  const t24 = `flex h-full flex-col px-3 ${isLanding || size === "small" ? "!pb-3 sm:!pb-0" : "pb-1"} sm:p-0 md:gap-3 ${size === "xSmall" ? "!gap-1 !px-0" : ""} ${["small", "xSmall"].includes(size) ? "" : "lg:flex-row"}`;
  const t25 = hotelDetail?.isInstant;
  let t26;
  if ($[70] !== hotelOffers?.offers) {
    t26 = hotelOffers?.offers || [];
    $[70] = hotelOffers?.offers;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  const t27 = hotelDetail?.propertyId;
  const t28 = showMap && !isMobile;
  let t29;
  if ($[72] !== advertisementBadge || $[73] !== categories || $[74] !== facilities || $[75] !== hasSnappProDiscount || $[76] !== hotelDetail || $[77] !== id || $[78] !== isHotelDomHomePage || $[79] !== isInternational || $[80] !== isSuggestedCard || $[81] !== linkProperties || $[82] !== loading || $[83] !== location || $[84] !== pricing || $[85] !== regularBadges || $[86] !== reservationInfo || $[87] !== size || $[88] !== snappPayData || $[89] !== t25 || $[90] !== t26 || $[91] !== t27 || $[92] !== t28 || $[93] !== transportation) {
    t29 = <HotelCardInfo {...hotelDetail} isHotelDomHomePage={isHotelDomHomePage} linkProperties={linkProperties} advertisementBadge={advertisementBadge} facilities={facilities} id={id} isInstant={t25} isSuggestedCard={isSuggestedCard} loading={loading} location={location} offers={t26} pricing={pricing} propertyId={t27} regularBadges={regularBadges} reservationInfo={reservationInfo} showMap={t28} size={size} snappPayData={snappPayData} categories={categories} hasSnappProDiscount={hasSnappProDiscount} transportation={transportation} isInternational={isInternational} />;
    $[72] = advertisementBadge;
    $[73] = categories;
    $[74] = facilities;
    $[75] = hasSnappProDiscount;
    $[76] = hotelDetail;
    $[77] = id;
    $[78] = isHotelDomHomePage;
    $[79] = isInternational;
    $[80] = isSuggestedCard;
    $[81] = linkProperties;
    $[82] = loading;
    $[83] = location;
    $[84] = pricing;
    $[85] = regularBadges;
    $[86] = reservationInfo;
    $[87] = size;
    $[88] = snappPayData;
    $[89] = t25;
    $[90] = t26;
    $[91] = t27;
    $[92] = t28;
    $[93] = transportation;
    $[94] = t29;
  } else {
    t29 = $[94];
  }
  let t30;
  if ($[95] !== size) {
    t30 = size !== "xSmall" && <div className="md:ps-4 lg:ps-0"><HotelCardDivider size={size} /></div>;
    $[95] = size;
    $[96] = t30;
  } else {
    t30 = $[96];
  }
  let t31;
  if ($[97] !== actionButtonText || $[98] !== clickLoading || $[99] !== hasSnappProDiscount || $[100] !== hotelDetail?.isAvailable || $[101] !== hotelDetail?.isFomo || $[102] !== hotelDetail?.roomCount || $[103] !== hotelDetail?.roomTitle || $[104] !== id || $[105] !== isHotelDomHomePage || $[106] !== isLanding || $[107] !== isVillaHomePage || $[108] !== linkProperties || $[109] !== loading || $[110] !== pricing || $[111] !== reservationInfo || $[112] !== size) {
    t31 = size === "xSmall" ? <HotelCardPricing {...pricing} size={size} hasSnappProDiscount={hasSnappProDiscount} /> : <HotelCardAction isLanding={isLanding} linkProperties={linkProperties} clickLoading={clickLoading === id} isAvailable={hotelDetail?.isAvailable} isFomo={hotelDetail?.isFomo} loading={loading} pricingInfo={pricing} reservationInfo={reservationInfo} roomCount={hotelDetail?.roomCount} roomTitle={hotelDetail?.roomTitle} size={size} hasSnappProDiscount={hasSnappProDiscount} isHotelDomHomePage={isHotelDomHomePage} isVillaHomePage={isVillaHomePage} actionButtonText={actionButtonText} />;
    $[97] = actionButtonText;
    $[98] = clickLoading;
    $[99] = hasSnappProDiscount;
    $[100] = hotelDetail?.isAvailable;
    $[101] = hotelDetail?.isFomo;
    $[102] = hotelDetail?.roomCount;
    $[103] = hotelDetail?.roomTitle;
    $[104] = id;
    $[105] = isHotelDomHomePage;
    $[106] = isLanding;
    $[107] = isVillaHomePage;
    $[108] = linkProperties;
    $[109] = loading;
    $[110] = pricing;
    $[111] = reservationInfo;
    $[112] = size;
    $[113] = t31;
  } else {
    t31 = $[113];
  }
  let t32;
  if ($[114] !== t24 || $[115] !== t29 || $[116] !== t30 || $[117] !== t31) {
    t32 = <div className={t24}>{t29}{t30}{t31}</div>;
    $[114] = t24;
    $[115] = t29;
    $[116] = t30;
    $[117] = t31;
    $[118] = t32;
  } else {
    t32 = $[118];
  }
  let t33;
  if ($[119] !== t23 || $[120] !== t32) {
    t33 = <div className={t23}>{t32}</div>;
    $[119] = t23;
    $[120] = t32;
    $[121] = t33;
  } else {
    t33 = $[121];
  }
  let t34;
  if ($[122] !== loading || $[123] !== pricing?.price || $[124] !== pricing?.priceOff || $[125] !== size || $[126] !== snappPayData) {
    t34 = size !== "small" && size !== "xSmall" && <div className="mt-2 block md:hidden"><HotelSnappPayBadge {...snappPayData} loading={loading} page="cityPage" price={pricing?.priceOff || pricing?.price} /></div>;
    $[122] = loading;
    $[123] = pricing?.price;
    $[124] = pricing?.priceOff;
    $[125] = size;
    $[126] = snappPayData;
    $[127] = t34;
  } else {
    t34 = $[127];
  }
  let t35;
  if ($[128] !== reference || $[129] !== t14 || $[130] !== t15 || $[131] !== t16 || $[132] !== t17 || $[133] !== t22 || $[134] !== t33 || $[135] !== t34) {
    t35 = <div className={t14} onMouseEnter={t15} onMouseLeave={t16} onClick={t17} ref={reference}>{t22}{t33}{t34}</div>;
    $[128] = reference;
    $[129] = t14;
    $[130] = t15;
    $[131] = t16;
    $[132] = t17;
    $[133] = t22;
    $[134] = t33;
    $[135] = t34;
    $[136] = t35;
  } else {
    t35 = $[136];
  }
  let t36;
  if ($[137] !== id || $[138] !== isDesktop || $[139] !== onClose || $[140] !== primaryBadge?.description || $[141] !== primaryBadge?.text || $[142] !== router.query.modal || $[143] !== size) {
    t36 = size !== "xSmall" && primaryBadge?.text && router?.query?.modal === `badge-info-${id}` && !isDesktop && <div className="block md:hidden"><MiniPopover onClose={onClose} open={router?.query?.modal === `badge-info-${id}`} title={primaryBadge?.text} className="!z-[100]"><div className="flex w-full flex-col gap-4 px-6 pb-6"><span className="text-body-2">{primaryBadge?.description}</span><Button color="secondary" onClick={onClose} text={"\u0645\u062A\u0648\u062C\u0647 \u0634\u062F\u0645"} variant="outline" /></div></MiniPopover></div>;
    $[137] = id;
    $[138] = isDesktop;
    $[139] = onClose;
    $[140] = primaryBadge?.description;
    $[141] = primaryBadge?.text;
    $[142] = router.query.modal;
    $[143] = size;
    $[144] = t36;
  } else {
    t36 = $[144];
  }
  let t37;
  if ($[145] !== t35 || $[146] !== t36) {
    t37 = <>{t35}{t36}</>;
    $[145] = t35;
    $[146] = t36;
    $[147] = t37;
  } else {
    t37 = $[147];
  }
  return t37;
};